import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AltRetailerLogo from 'images/vr-new-retail_3.png'
import { ImageIcon } from 'lucide-react'
import TWControls from 'components/controls/TWControls'
import { Form, useForm } from 'components/controls/useForm'
import { Checkbox } from 'components/ui/checkbox'
import { Label } from 'components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { MapPin, ImageIcon as ImageSearchIcon } from 'lucide-react'
import ImagePickerPopup from 'components/popups/ImagePickerPopup'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { fetchLookupLists } from 'slices/lookupListSlice'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import type { AddressResult } from 'components/AddressAutocomplete'
import geocodeAddress from 'functions/geocodeAddress'
import { toast } from 'components/ui/toast-store'
import {
	planSiteRegeocode,
	siteAddressSignature,
	siteAddressQuery,
	LOCATION_SIGNATURE_FIELDS,
} from 'utils/siteRegeocode'

interface LocationEditPopupCNProps {
	editLocation: (values: any) => void
	recordForEdit: any
	/**
	 * Render the Active / POI Only / Tenant Only block. Defaults to true so a
	 * surface has to opt OUT — Admin > Bulk Load is the only place those two
	 * flags can be set on an imported row (bulkLocationController persists
	 * them), and a default of false would let a future caller lose that
	 * without noticing.
	 *
	 * Hiding the block does not drop the values: initialValues spreads
	 * recordForEdit and the submit handler sends `values` wholesale, so the
	 * three flags round-trip untouched. That matters, because
	 * locationController assigns them unconditionally on update — a payload
	 * that omitted them would overwrite what is stored.
	 */
	showStatus?: boolean
}

/**
 * The address fields that decide where the pin sits, as one normalised string.
 * Comparing signatures rather than individual fields means a casing or
 * whitespace edit does not read as the location having moved.
 *
 * These delegate to the shared helpers with the location field list rather
 * than keeping a second copy of it. The copy was the drift risk: this screen
 * and the site edit sheet make the same judgement about the same kind of data,
 * and a field list that fell out of step here would show up as one of them
 * protecting a hand-placed pin while the other quietly overwrote it.
 */
const addressSignature = (v: any): string => siteAddressSignature(v, LOCATION_SIGNATURE_FIELDS)

/** The same fields as a free-text query for the geocoder. */
const addressQuery = (v: any): string => siteAddressQuery(v, LOCATION_SIGNATURE_FIELDS)

const LocationEditPopupCN = ({ editLocation, recordForEdit, showStatus = true }: LocationEditPopupCNProps) => {
	const dispatch = useDispatch()
	const { entities: lookupEntities, ids: lookupIds, isInitialized: lookupLoaded } = useSelector((state: any) => state.lookupLists)

	useEffect(() => {
		if (!lookupLoaded) dispatch(fetchLookupLists(undefined) as any)
	}, [dispatch, lookupLoaded])

	const locationTypes = useMemo(
		() =>
			lookupIds
				.map((id: string) => lookupEntities[id])
				.filter((item: any) => item && item.listType === 'location')
				.map((item: any) => ({ selectValue: item.option }))
				.sort((a: any, b: any) => a.selectValue.localeCompare(b.selectValue)),
		[lookupEntities, lookupIds],
	)

	const initialValues = {
		...recordForEdit,
	}

	const [openImagePicker, setOpenImagePicker] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	/**
	 * The values held back while the user rules on a hand-placed pin, and the
	 * message explaining what they stand to lose.
	 *
	 * The message comes from `planSiteRegeocode` rather than being written
	 * here, so it describes the provenance that actually applies: a GPS fix
	 * quotes its accuracy, a hand-placed pin does not claim one it never had.
	 */
	const [pinPrompt, setPinPrompt] = useState<{ values: any; message: string } | null>(null)

	// The address the current latitude/longitude belong to. It starts as the
	// stored address and moves forward whenever coordinates arrive from a
	// source we trust (an autocomplete pick, or the user typing them in), so
	// Update only re-geocodes an address that has genuinely drifted from its
	// pin, and an edit to a name or a checkbox costs no lookup.
	const geocodedAddressRef = useRef(addressSignature(recordForEdit))

	/**
	 * Whether the coordinates were placed by hand is no longer tracked here.
	 *
	 * It used to be a ref, which meant it survived only as long as the popup
	 * stayed open: type coordinates today, reopen next week to fix a typo in
	 * the address, and the re-geocode overwrote them silently because nothing
	 * remembered they were deliberate.
	 *
	 * `capturedVia` now rides on the record itself and is saved with it, so the
	 * judgement is the same one the site sheet makes, from the same tested
	 * function, and it lasts. MYD-435.
	 */

	const validate = () => {
		const temp: any = { ...errors }
		temp.assetName = values.assetName ? '' : 'This field is required.'

		setErrors({
			...temp,
		})

		return Object.values(temp).every((x: any) => x === '')
	}

	const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(initialValues, true, validate)

	/**
	 * Save, moving the pin to follow the address.
	 *
	 * @param movePin False keeps the coordinates exactly as they are.
	 */
	const saveWith = async (source: any, movePin: boolean) => {
		let toSave = source
		const signature = addressSignature(source)
		const query = addressQuery(source)

		if (movePin && query) {
			setIsSaving(true)
			try {
				const { lat, lng } = await geocodeAddress(query)
				toSave = { ...source, latitude: String(lat), longitude: String(lng) }
				setValues(toSave)
				geocodedAddressRef.current = signature
			} catch (err) {
				// Saving the edit still matters more than the pin, so the update
				// goes through either way. Say so, though, rather than leaving
				// the marker silently pointing at the old address.
				console.warn('[LocationEditPopupCN] Re-geocode failed', err)
				toast.warning(
					'The address was saved, but it could not be geocoded. The map pin still points at the previous location.',
				)
			} finally {
				setIsSaving(false)
			}
		} else if (!movePin) {
			// The address it was asked about is now the one these coordinates
			// stand for, so saving again does not ask a second time.
			geocodedAddressRef.current = signature
		}

		setPinPrompt(null)
		editLocation(toSave)
	}

	const submitHandler = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!validate()) return

		/*
			The same tested decision the site edit sheet makes, from the same
			function, now that a location carries the provenance it needs.

			A legacy row has no `capturedVia`, so it reads as derived and keeps
			today's silent re-geocode. That is deliberate: treating every
			pre-MYD-435 location as hand-placed would prompt on all of them and
			teach people to dismiss the prompt, which costs more than it saves.
		*/
		const plan = planSiteRegeocode(values, geocodedAddressRef.current, values, {
			fields: LOCATION_SIGNATURE_FIELDS,
			noun: 'location',
		})

		if (plan.action === 'confirm') {
			setPinPrompt({ values, message: plan.prompt ?? '' })
			return
		}

		await saveWith(values, plan.action === 'geocode')
	}

	useEffect(() => {
		if (recordForEdit != null) {
			setValues({ ...recordForEdit })
			geocodedAddressRef.current = addressSignature(recordForEdit)
			// Nothing to reset about provenance any more: it arrives on the
			// record, so switching records swaps it automatically rather than
			// needing to be cleared. That was the bug class the ref invited.
			setPinPrompt(null)
		}
	}, [recordForEdit, setValues])

	const convertToDefEventPara = (name: string, value: any) => ({
		target: { name, value },
	})

	const imageSearchQuery =
		values.assetName && values.city && values.stateProvince
			? `${values.assetName} ${values.city} ${values.stateProvince}`
			: values.assetName || null

	const handleAddressSelect = async (result: AddressResult) => {
		const street = [result.address.house_number, result.address.road]
			.filter(Boolean)
			.join(' ')
			.trim()
		const picked = {
			...values,
			assetName: result.name || values.assetName,
			address: street || values.address,
			city: result.address.city || values.city,
			stateProvince: result.address.state || values.stateProvince,
			postalCode: result.address.postcode || values.postalCode,
			country: result.address.country || values.country,
			latitude: String(result.latitude),
			longitude: String(result.longitude),
			// The geocoder's position, not the user's, so a later address edit
			// is free to move it without asking. Recorded rather than inferred,
			// because next week nothing else will remember.
			capturedVia: 'address',
		}
		setValues(picked)
		// These coordinates came straight from the pick, so they already belong
		// to this address and Update must not geocode over them.
		geocodedAddressRef.current = addressSignature(picked)

		// Auto-pull a Google Places photo on selection. Failures are silent —
		// the user can still pick manually via ImagePickerPopup. Honours
		// project_photo_requirement.md.
		if (result.photoUrl) {
			try {
				const upload = await uploadToCloudinary(result.photoUrl, { resourceType: 'image' })
				setValues(prev => ({ ...prev, image: upload.secure_url }))
			} catch (err) {
				console.warn('[LocationEditPopupCN] Auto photo upload failed', err)
			}
		}
	}

	// Typing coordinates in by hand is an explicit override: pair them with
	// whatever address is in the form so Update leaves them as entered.
	/**
	 * Typing a coordinate adopts the current address as the one it belongs to,
	 * so Update does not immediately geocode over what was just entered, and
	 * marks the pin as placed by hand.
	 */
	const handleCoordinateChange = (e: any) => {
		handleInputChange(e)
		geocodedAddressRef.current = addressSignature(values)
		// 'map' is the model's word for "a person put it here", covering both a
		// pin dragged on a map and coordinates typed into these inputs. The
		// distinction that matters is deliberate versus derived, and this is the
		// deliberate side. Saved with the record, so it outlives this popup.
		setValues((prev: any) => ({ ...prev, capturedVia: 'map' }))
	}

	const proximityForSearch =
		values.latitude && values.longitude && Number.isFinite(Number(values.latitude)) && Number.isFinite(Number(values.longitude))
			? { lat: Number(values.latitude), lng: Number(values.longitude) }
			: undefined

	return (
		<div className="max-w-[960px] mx-auto">
			<Form onSubmit={submitHandler}>
				<div className="grid grid-cols-12 gap-4">
					<div className="col-span-12">
						<Label className="mb-1.5 block text-xs font-semibold text-gray-500 uppercase tracking-wide">
							Update Address by Search
						</Label>
						<AddressAutocomplete
							placeholder="Search by address or business name to re-geocode..."
							proximity={proximityForSearch}
							onSelect={handleAddressSelect}
						/>
					</div>
					<div className="col-span-6">
						<TWControls.Input
							size="small"
							name="assetName"
							label="Location Name"
							value={values.assetName}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.Input
							size="small"
							name="retailer"
							label="Retailer"
							value={values.retailer}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.SelectType
							className="feed_select"
							name="locationType"
							label="Location Type"
							value={values.locationType}
							onChange={handleInputChange}
							options={locationTypes}
						/>
					</div>

					<div className="col-span-6">
						<TWControls.Input
							size="small"
							name="address"
							label="Address"
							value={values.address}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-6">
						<TWControls.Input
							size="small"
							name="assetLink"
							label="Website"
							value={values.assetLink}
							onChange={handleInputChange}
						/>
					</div>

					<div className="col-span-3">
						<TWControls.Input size="small" name="city" label="City" value={values.city} onChange={handleInputChange} />
					</div>
					<div className="col-span-2">
						<TWControls.Input
							size="small"
							name="stateProvince"
							label="State/Prov"
							value={values.stateProvince}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-2">
						<TWControls.Input
							size="small"
							name="postalCode"
							label="Postal Code"
							value={values.postalCode}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-2">
						<TWControls.Input
							size="small"
							name="country"
							label="Country"
							value={values.country}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.Input
							size="small"
							name="phoneNumber"
							label="Phone Number"
							value={values.phoneNumber}
							onChange={handleInputChange}
						/>
					</div>

					<div className="col-span-3">
						<TWControls.Input
							size="small"
							name="latitude"
							label="Latitude"
							value={values.latitude}
							onChange={handleCoordinateChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.Input
							size="small"
							name="longitude"
							label="Longitude"
							value={values.longitude}
							onChange={handleCoordinateChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.Input
							size="small"
							name="totalSqft"
							label="SQFT"
							value={values.totalSqft}
							onChange={handleInputChange}
						/>
					</div>
					<div className="col-span-3">
						<TWControls.Input size="small" name="apn" label="APN" value={values.apn} onChange={handleInputChange} />
					</div>
				</div>

				{/* Status Checkboxes */}
				{showStatus && (
				<div className="mt-6 mb-4">
					<Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</Label>
					<div className="flex items-start gap-8 mt-3">
						<div className="flex items-start gap-2.5 cursor-pointer">
							<Checkbox
								checked={!!values.isActive}
								onCheckedChange={(checked: boolean) => handleInputChange(convertToDefEventPara('isActive', checked))}
								className="mt-0.5"
							/>
							<div>
								<span className="text-sm font-medium text-gray-800">Active</span>
								<p className="text-xs text-gray-500 mt-0.5">
									Location is currently active in your portfolio and workflows.
								</p>
							</div>
						</div>

						<div className="flex items-start gap-2.5 cursor-pointer">
							<Checkbox
								checked={!!values.isPOIOnly}
								onCheckedChange={(checked: boolean) => handleInputChange(convertToDefEventPara('isPOIOnly', checked))}
								className="mt-0.5"
							/>
							<div>
								<span className="text-sm font-medium text-gray-800">POI Only</span>
								<p className="text-xs text-gray-500 mt-0.5">
									Location is tracked as a point of interest only (not an operating site).
								</p>
							</div>
						</div>

						<div className="flex items-start gap-2.5 cursor-pointer">
							<Checkbox
								checked={!!values.isTenantOnly}
								onCheckedChange={(checked: boolean) =>
									handleInputChange(convertToDefEventPara('isTenantOnly', checked))
								}
								className="mt-0.5"
							/>
							<div>
								<span className="text-sm font-medium text-gray-800">Tenant Only</span>
								<p className="text-xs text-gray-500 mt-0.5">Location is tracked as a co-tenant reference only.</p>
							</div>
						</div>
					</div>
				</div>
				)}

				{/* Image + Actions */}
				<div className="flex flex-col items-center justify-center mt-4 mb-2">
					<img
						className="w-[180px] mb-4 rounded border-2 border-[#0080fe]"
						src={values.image || AltRetailerLogo}
						alt={values.assetName || ''}
					/>
					<button
						type="button"
						onClick={() => setOpenImagePicker(true)}
						className="inline-flex items-center gap-1.5 text-sm text-[#0080fe] hover:text-[#0070e0] transition-colors"
					>
						<ImageIcon className="h-3.5 w-3.5" />
						{values.image ? 'Update Image' : 'Add Image'}
					</button>
				</div>

				<div className="flex items-center justify-center mt-4">
					<button
						type="submit"
						disabled={isSaving}
						className="rounded-lg bg-[#0080fe] px-6 py-2 text-sm font-medium text-white hover:bg-[#0070e0] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{isSaving ? 'Locating address...' : 'Update'}
					</button>
				</div>
			</Form>

			{/* Image Picker Dialog */}
			<Dialog open={openImagePicker} onClose={() => setOpenImagePicker(false)}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<ImageSearchIcon className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Select Image</DialogTitle>
								<DialogDescription>Choose an image for "{values.assetName}"</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<ImagePickerPopup
							placeId={values.googlePlaceId}
							searchQuery={imageSearchQuery}
							locationName={values.assetName}
							onImageSelected={(imageUrl: string) => {
								setValues({ ...values, image: imageUrl })
								setOpenImagePicker(false)
							}}
						/>
					</div>
				</DialogContent>
			</Dialog>

			{/*
				Asked only when the user typed coordinates in THIS session and then
				changed the address. A location stores no provenance, so once the
				popup closes there is no way to know a pin was placed deliberately.
				Keeping it is the default: both choices save the edit, and only the
				pin differs.
			*/}
			<Dialog open={Boolean(pinPrompt)} onClose={() => setPinPrompt(null)}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Move the map pin?</DialogTitle>
						<DialogDescription>{pinPrompt?.message}</DialogDescription>
					</DialogHeader>
					<p className="mt-3 text-sm text-gray-600">
						An address lookup returns roughly where the street is, which is usually
						less precise than a position somebody set deliberately.
					</p>
					<div className="flex justify-end gap-2 p-5 pt-4">
						<button
							type="button"
							onClick={() => void saveWith(pinPrompt?.values, true)}
							disabled={isSaving}
							className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-60"
						>
							{isSaving ? 'Saving...' : 'Move the pin'}
						</button>
						<button
							type="button"
							onClick={() => void saveWith(pinPrompt?.values, false)}
							disabled={isSaving}
							className="rounded-lg bg-[#0080fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#0070e0] disabled:opacity-60"
						>
							{isSaving ? 'Saving...' : 'Keep my coordinates'}
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default LocationEditPopupCN
